import React from 'react';
import { EditorQuill } from 'ui/EditorQuill/EditorQuill';
import { TextInput } from 'ui/TextInput';
import FluidButton from 'ui/FluidButton';
import { CircleIconButton } from 'ui/CircleIconButton';
import { IContentSection } from '../types';
import XIcon from 'ui/Icons/tabler/x.component.svg';

interface IValidationErrors {
  title?: boolean;
  crmCompanyUuid?: boolean;
  categoryUuid?: boolean;
  shortDescriptionTitle?: boolean;
  shortDescriptionContent?: boolean;
}

interface IContentDetailsSectionProps {
  shortDescriptionTitle: string;
  shortDescriptionContent: string;
  sections: IContentSection[];
  validationErrors: IValidationErrors;
  updateShortDescriptionTitle: (title: string) => void;
  updateShortDescriptionContent: (content: string) => void;
  addSection: () => void;
  removeSection: (sectionId: string) => void;
  updateSectionTitle: (sectionId: string, title: string) => void;
  updateSectionContent: (sectionId: string, content: string) => void;
}

export const ContentDetailsSection = ({
  shortDescriptionTitle,
  shortDescriptionContent,
  sections,
  validationErrors,
  updateShortDescriptionTitle,
  updateShortDescriptionContent,
  addSection,
  removeSection,
  updateSectionTitle,
  updateSectionContent,
}: IContentDetailsSectionProps) => {
  return (
    <div className="space-y-[10px]">
      {/* Section Title */}
      <span className="font-bold text-[17px] text-gray-800 font-pt-sans">Content Details</span>

      {/* Short Description - Always visible */}
      <div className="space-y-2">
        <label className="block text-[13px] font-pt-sans text-gray-700 mb-1 tracking-wider">
          Short Description *
        </label>

        {/* Text input field */}
        <div data-validation-id="shortDescriptionTitle">
          <TextInput
            id="short-description-title"
            value={shortDescriptionTitle}
            onChange={(e) => updateShortDescriptionTitle(e.currentTarget.value)}
            placeholder="Overview"
            errorMessage={validationErrors.shortDescriptionTitle ? 'Field is required' : undefined}
          />
        </div>

        {/* Rich text editor */}
        <div data-validation-id="shortDescriptionContent" className={validationErrors.shortDescriptionContent ? 'border border-red-95 bg-red-25 rounded' : ''}>
          <EditorQuill
            text={shortDescriptionContent}
            onChange={updateShortDescriptionContent}
            placeholder=""
            minHeight={72}
          />
        </div>
        {validationErrors.shortDescriptionContent && (
          <p className="text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0">Field is required</p>
        )}
      </div>

      {/* Dynamic Sections */}
      {sections.map((section, index) => (
        <div key={section.id} className="flex items-center gap-2.5">
          {/* Section Form Fields */}
          <div className="flex-1 space-y-2">
            {/* Section Header */}
            <label className="block text-[13px] font-pt-sans text-gray-700 tracking-wider">
              Section
            </label>
            
            {/* Section Title Input */}
            <TextInput
              id={`section-title-${section.id}`}
              value={section.title}
              onChange={(e) => updateSectionTitle(section.id, e.currentTarget.value)}
              placeholder="Section title"
            />

            {/* Section Content Editor */}
            <EditorQuill
              text={section.content}
              onChange={(content) => updateSectionContent(section.id, content)}
              placeholder="Section content..."
              minHeight={72}
            />
          </div>
          
          {/* Delete Button */}
          <div className="flex items-center">
            <CircleIconButton
              type="secondary"
              IconComponent={XIcon}
              iconSize="sm"
              onClick={() => removeSection(section.id)}
            />
          </div>
        </div>
      ))}

      {/* Add Section Button */}
      <div className="flex justify-start">
        <FluidButton
          type="secondary"
          onClick={addSection}
          className="bg-white border border-brown-prime text-brown-prime hover:bg-brown-prime hover:text-white"
        >
          Add Section
        </FluidButton>
      </div>
    </div>
  );
};